import FeatureAttachments from "components/feature-attachments";
import {
  EntityType,
  ESubmittalStatus,
  EUserTypes,
  ProjectPermissionEnum
} from "constants/index";
import {
  isPermissionNotGrantted,
  ProjectContext,
  TProjectContext
} from "context/ProjectProvider";
import { TFetureDetailUpdateAction } from "entity-app/models";
import EditableDetailsCardWrapper from "entity-app/shared-components/editable-details-card-wrapper/editable-details-card-wrapper";
import { useContext, useMemo, useState } from "react";
import {
  useIntegrationProjectParticipants,
  useProjectParticipants
} from "hooks/project-participants";
import { isProjectInIntegrationMode, matchUserProjectRole } from "utils/utils";
import { ErrorBoundary } from "@sentry/react";
import { Link, useParams } from "react-router-dom";
import { FeatureTypes } from "entity-app/constants";
import CustomFields from "components/custom-fields";
import SubmittalWorkflowResponse from "./components/submittal-workflow-response";
import SubmittalLinkedTopSection from "./components/submittal-linked-top-section";
import SubmittalGeneralInfo from "./components/submittal-general-info";
import SubmittalWorkflow from "./components/submittal-workflow";
import { SubmittalDetailLinks } from "./components/submittal-details-links";
import FeatureHistory from "../../../components/feature-history/feature-history";

function SubmittalDetailsCards(
  props: Readonly<{
    featureInstanceData: any;
    actions: TFetureDetailUpdateAction;
    featureId: string;
    setTabIndex: (tab: string) => void;
    setAddMore: any;
    workflowOverride: {
      isWorkflowInOverrideMode: boolean;
      setIsWorkflowInOverrideMode: (value: boolean) => void;
    };
    isRevisionMode: boolean;
    submittal: any;
    editableFields: any[];
    updateFeatureData: {
      localFeatureInstanceData: any;
      setLocalFeatureInstanceData: (value: any) => void;
    };
    submittalViewState: any;
  }>
) {
  const {
    featureInstanceData,
    actions,
    featureId,
    setTabIndex,
    setAddMore,
    workflowOverride: { isWorkflowInOverrideMode, setIsWorkflowInOverrideMode },
    isRevisionMode,
    submittal,
    editableFields,
    updateFeatureData: {
      localFeatureInstanceData,
      setLocalFeatureInstanceData
    },
    submittalViewState
  } = props;
  const {
    tokenContents,
    projectDetails,
    tokenRetrievalState,
    gqlClientForProject,
    columnHeaders: { submittalCustomColumns, submittalHeaderMap },
    isFeatureFlagEnabled,
    isProjectArchived
  }: TProjectContext = useContext(ProjectContext);
  const { projectId } = useParams() as any;

  const isLoggedInUserGC = matchUserProjectRole(
    EUserTypes.GENERAL_CONTRACTOR,
    tokenContents?.role
  );

  const isAttachementFeatureEnabled =
    !isFeatureFlagEnabled("DISABLE_ATTACHMENT");

  const isIntegrationMode = isProjectInIntegrationMode(
    projectDetails ? projectDetails.mode : 0
  );

  const { projectParticipants: WFProjectParticipants } = useProjectParticipants(
    isIntegrationMode || !isLoggedInUserGC
  );
  const { data: INTGProjectParticipants } = useIntegrationProjectParticipants(
    !isIntegrationMode || !isLoggedInUserGC
  );

  const projectParticipants = isIntegrationMode
    ? INTGProjectParticipants
    : WFProjectParticipants;

  const canEditLinkingPage = !isPermissionNotGrantted(
    ProjectPermissionEnum.EditLinkingPage,
    tokenContents?.role ?? ""
  );

  const isVoidSubmittal: boolean = useMemo(() => {
    return submittal?.workflow_status === ESubmittalStatus.VOID;
  }, [submittal?.workflow_status]);

  const isDeletedSubmittal: boolean = useMemo(() => {
    return submittal?.deleted_from_source;
  }, [submittal?.deleted_from_source]);

  const [isDateblockRecalculating, setIsDateblockRecalculating] =
    useState(false);

  return (
    <div
      className="flex flex-col p-4 space-y-4"
      data-testid="submittal-cards-root"
    >
      <SubmittalLinkedTopSection
        actions={actions}
        featureInstanceData={localFeatureInstanceData}
        isRevisionMode={isRevisionMode}
        isDateblockRecalculating={isDateblockRecalculating}
        setIsDateblockRecalculating={setIsDateblockRecalculating}
        data-testid="submittal-cards-linked-top-section"
      />
      <div className="grid grid-cols-3 gap-4 pb-10">
        <EditableDetailsCardWrapper
          title="General Information"
          data-testid="submittal-cards-general-info-card"
        >
          <ErrorBoundary>
            <SubmittalGeneralInfo
              featureInstanceData={featureInstanceData}
              actions={actions}
              submittal={submittal}
              editableFields={editableFields}
              isRevisionMode={isRevisionMode}
              updateFeatureData={{
                localFeatureInstanceData,
                setLocalFeatureInstanceData
              }}
              data-testid="submittal-cards-general-info"
            />
          </ErrorBoundary>
        </EditableDetailsCardWrapper>
        <EditableDetailsCardWrapper
          title="Workflow"
          data-testid="submittal-cards-workflow-card"
        >
          <ErrorBoundary>
            <SubmittalWorkflow
              featureInstanceData={featureInstanceData}
              isRevisionMode={isRevisionMode}
              actions={actions}
              projectParticipants={projectParticipants}
              submittal={submittal}
              editableFields={editableFields}
              updateFeatureData={{
                localFeatureInstanceData,
                setLocalFeatureInstanceData
              }}
              submittalViewState={submittalViewState}
              data-testid="submittal-cards-workflow"
            />
          </ErrorBoundary>
        </EditableDetailsCardWrapper>
        <EditableDetailsCardWrapper
          title="Links"
          extra={
            canEditLinkingPage &&
            !isVoidSubmittal &&
            !isDeletedSubmittal &&
            featureInstanceData?.is_tracked ? (
              <Link
                to={`/project/${projectId}/schedule/submittal-linking?&SubmittalId=${featureId}`}
                data-testid="submittal-cards-manage-link"
              >
                Manage
              </Link>
            ) : null
          }
          data-testid="submittal-cards-links-card"
        >
          <ErrorBoundary>
            <div
              className="h-[396px] overflow-auto"
              data-testid="submittal-cards-links-container"
            >
              <SubmittalDetailLinks
                featureInstanceData={featureInstanceData}
                canEditLinkingPage={canEditLinkingPage}
                isRevisionMode={isRevisionMode}
                isVoidSubmittal={isVoidSubmittal}
                isDeletedSubmittal={isDeletedSubmittal}
                isTrackedSubmittal={featureInstanceData?.is_tracked}
                data-testid="submittal-cards-links"
              />
            </div>
          </ErrorBoundary>
        </EditableDetailsCardWrapper>

        {!isIntegrationMode && (
          <>
            {isAttachementFeatureEnabled && (
              <EditableDetailsCardWrapper
                title="Attachments"
                data-testid="submittal-cards-attachments-card"
              >
                <ErrorBoundary>
                  <div
                    className="h-[320px] overflow-auto"
                    data-testid="submittal-cards-attachments-container"
                  >
                    <FeatureAttachments
                      featureId={featureId}
                      setTabIndex={setTabIndex}
                      disabled={
                        !isWorkflowInOverrideMode &&
                        (isRevisionMode ||
                          isPermissionNotGrantted(
                            ProjectPermissionEnum.AddAttachment,
                            tokenContents?.role ?? ""
                          ) ||
                          !editableFields.includes("attachment"))
                      }
                      featureType={EntityType.Submittal}
                      setAddMore={setAddMore}
                      isRevisionMode={isRevisionMode}
                      featureInstanceData={localFeatureInstanceData}
                      data-testid="submittal-cards-attachments"
                    />
                  </div>
                </ErrorBoundary>
              </EditableDetailsCardWrapper>
            )}
            <EditableDetailsCardWrapper
              title="Workflow Response"
              extra={
                isWorkflowInOverrideMode && (
                  <span className="text-sm text-amber-600">
                    You are overriding the workflow
                  </span>
                )
              }
              data-testid="submittal-cards-workflow-response-card"
            >
              <ErrorBoundary>
                <div
                  className=""
                  data-testid="submittal-cards-workflow-response-container"
                >
                  <SubmittalWorkflowResponse
                    actions={actions}
                    projectId={projectId}
                    submittal={submittal}
                    gqlClientForProject={gqlClientForProject}
                    isRevisionMode={isRevisionMode}
                    editableFields={editableFields}
                    isAttachementFeatureEnabled={isAttachementFeatureEnabled}
                    workflowOverride={{
                      isWorkflowInOverrideMode,
                      setIsWorkflowInOverrideMode
                    }}
                    tokenRetrievalState={tokenRetrievalState}
                    submittalViewState={submittalViewState}
                    updateFeatureData={{
                      localFeatureInstanceData,
                      setLocalFeatureInstanceData
                    }}
                    setIsDateblockRecalculating={setIsDateblockRecalculating}
                    isDateblockRecalculating={isDateblockRecalculating}
                    submittalHeaderMap={submittalHeaderMap}
                    data-testid="submittal-cards-workflow-response"
                  />
                </div>
              </ErrorBoundary>
            </EditableDetailsCardWrapper>
            <EditableDetailsCardWrapper
              title="Workflow History"
              data-testid="submittal-cards-workflow-history-card"
            >
              <ErrorBoundary>
                <div
                  className="h-[320px] overflow-auto"
                  data-testid="submittal-cards-workflow-history-container"
                >
                  <FeatureHistory
                    featureInstanceData={localFeatureInstanceData}
                    featureType={FeatureTypes.SUBMITTALS}
                    data-testid="submittal-cards-workflow-history"
                  />
                </div>
              </ErrorBoundary>
            </EditableDetailsCardWrapper>
          </>
        )}

        {submittalCustomColumns?.length > 0 && (
          <EditableDetailsCardWrapper
            title="Others"
            data-testid="material-detail-cards-others-wrapper"
          >
            <ErrorBoundary>
              <CustomFields
                featureInstanceData={featureInstanceData}
                data-testid="submittal-detail-cards-others"
                type={FeatureTypes.SUBMITTALS}
                columns={submittalCustomColumns}
                disabled={
                  !!isProjectArchived ||
                  (!isWorkflowInOverrideMode && isVoidSubmittal)
                }
              />
            </ErrorBoundary>
          </EditableDetailsCardWrapper>
        )}
      </div>
    </div>
  );
}
export default SubmittalDetailsCards;
